/**
 * KBArticleService
 * CRUD for KBArticle with publish/archive transitions.
 * Route: api/kb-articles
 */
import { BaseService } from '../base/BaseService';
/** Default empty audience used when the API returns nothing parseable. */
const EMPTY_AUDIENCE = { roleNames: [], ministryIds: [], departmentIds: [] };
export class KBArticleService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? KBArticleService.DEFAULT_SERVICE_PATH,
            basePath: KBArticleService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    getAllRaw() {
        return this.http.get(this.buildUrl(''));
    }
    async getById(id) {
        const response = await this.getByIdRaw(id);
        return response.data ?? null;
    }
    getByIdRaw(id) {
        return this.http.get(this.buildUrl(`${id}`));
    }
    async getByCategory(kbCategoryId) {
        const response = await this.getByCategoryRaw(kbCategoryId);
        return response.data ?? [];
    }
    getByCategoryRaw(kbCategoryId) {
        return this.http.get(this.buildUrl(`by-category/${kbCategoryId}`));
    }
    async getByMinistry(ministryId) {
        const response = await this.getByMinistryRaw(ministryId);
        return response.data ?? [];
    }
    getByMinistryRaw(ministryId) {
        return this.http.get(this.buildUrl(`by-ministry/${ministryId}`));
    }
    async getBySlug(slug) {
        const response = await this.getBySlugRaw(slug);
        return response.data ?? null;
    }
    getBySlugRaw(slug) {
        return this.http.get(this.buildUrl(`by-slug/${encodeURIComponent(slug)}`));
    }
    async create(item) {
        const response = await this.createRaw(item);
        return response.data ?? null;
    }
    createRaw(item) {
        return this.http.post(this.buildUrl(''), item);
    }
    async update(id, item) {
        const response = await this.updateRaw(id, item);
        return response.data ?? null;
    }
    updateRaw(id, item) {
        return this.http.put(this.buildUrl(`${id}`), item);
    }
    /** Publish the article — stamps PublishedDateTime, fires KBArticlePublishedEvent. */
    async publish(id) {
        const response = await this.http.post(this.buildUrl(`${id}/publish`), null);
        return response.data ?? null;
    }
    /** Archive the article — fires KBArticleArchivedEvent, signals AI indexer to remove. */
    async archive(id) {
        const response = await this.http.post(this.buildUrl(`${id}/archive`), null);
        return response.data ?? null;
    }
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    deleteRaw(id) {
        return this.http.delete(this.buildUrl(`${id}`));
    }
    // ============================================================
    // Visibility — audience management (Restricted articles only)
    // ============================================================
    /**
     * Read the role/ministry/department audience grants for a Restricted
     * KB article. Empty lists == visible to all signed-in users.
     */
    async getAudience(kbArticleId) {
        const response = await this.getAudienceRaw(kbArticleId);
        return response.data ?? { ...EMPTY_AUDIENCE };
    }
    getAudienceRaw(kbArticleId) {
        return this.http.get(this.buildUrl(`${kbArticleId}/audience`));
    }
    /** Wholesale replace a KB article's audience grants. */
    async setAudience(kbArticleId, audience) {
        const response = await this.setAudienceRaw(kbArticleId, audience);
        return response.data ?? audience;
    }
    setAudienceRaw(kbArticleId, audience) {
        return this.http.put(this.buildUrl(`${kbArticleId}/audience`), audience);
    }
}
KBArticleService.DEFAULT_SERVICE_PATH = 'resources';
KBArticleService.BASE_PATH = 'api/kb-articles';
//# sourceMappingURL=KBArticleService.js.map